import {Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {RecordTableAction} from "../../types/record-table-action.types";
import {ResourceParams} from "../../models/resource/resources";
import {Subscription} from "rxjs";
import {NavigationEnd, Router} from "@angular/router";
import {
  RecordsDataRequestQueryParamsService
} from "../records-table-container/services/records-data-request-query-params.service";
import {MatOption, MatSelect, MatSelectChange} from "@angular/material/select";
import {dropdownSettingsResources} from "../../models/resource/tables/dropdown-settings-resource-type";
import {MatButton, MatIconButton} from "@angular/material/button";
import {MatInput} from "@angular/material/input";
import {MatSlideToggle} from "@angular/material/slide-toggle";
import {DEFAULT_ZOOM, ZOOM_OPTIONS} from "../../constants/constants";
import {RecordsTableToolbarService} from "./services/records-table-toolbar.service";
import {MatIcon} from "@angular/material/icon";
import {ActionDropdownMenuComponent} from "../action-dropdown-menu/action-dropdown-menu.component";
import {
  ToggleButtonsParams,
  UpdateQueryParamsProps,
  UpdateQueryParamsPropsKey
} from "./types/records-table-toolbar.types";
import {
  actionButtons,
  toggleButtons,
  toggleGroupActionButtons
} from "./configs/records-table-toolbar-config";
import {defaultResourceParams} from "../../models/resource/default-resource-params";
import {MatTooltip} from "@angular/material/tooltip";
import {FormsModule} from "@angular/forms";

@Component({
  selector: 'app-records-table-toolbar',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatInput,
    MatOption,
    MatSelect,
    MatSlideToggle,
    ActionDropdownMenuComponent,
    MatTooltip,
    FormsModule
  ],
  templateUrl: './records-table-toolbar.component.html',
  styleUrl: './records-table-toolbar.component.scss'
})
export class RecordsTableToolbarComponent implements OnInit, OnDestroy {
  @Input({ required: true }) resourceParams: ResourceParams = defaultResourceParams;
  @Input() isNewRecordDataValid: boolean = false;
  @Input() recordTableActions: Array<RecordTableAction> = [];
  @Input() currentZoomOption: number = DEFAULT_ZOOM;
  @Input() selectedRowId: number | null = null;
  @Output() editModeStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() updateQueryParams: EventEmitter<UpdateQueryParamsProps> = new EventEmitter<UpdateQueryParamsProps>();
  @Output() addRecordStateChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() saveRecordButtonClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() zoomChanged: EventEmitter<number> = new EventEmitter<number>();
  @Output() actionSelected: EventEmitter<RecordTableAction> = new EventEmitter<RecordTableAction>();
  @Output() refreshTable: EventEmitter<void> = new EventEmitter<void>();
  @Output() addCooperationOffer: EventEmitter<void> = new EventEmitter<void>();
  @Output() addUser: EventEmitter<void> = new EventEmitter<void>();
  @Output() addPriceLevelConfig: EventEmitter<void> = new EventEmitter<void>();
  @Output() addTelegramAccount: EventEmitter<void> = new EventEmitter<void>();
  @Output() createBloggerGroup: EventEmitter<void> = new EventEmitter<void>();
  @Output() openBloggerGroups: EventEmitter<void> = new EventEmitter<void>();
  @Output() openSendMassOffers: EventEmitter<void> = new EventEmitter<void>();
  @Output() createComponent: EventEmitter<void> = new EventEmitter<void>();
  @Output() createTestingResult: EventEmitter<void> = new EventEmitter<void>();
  @Output() createQualityControl: EventEmitter<void> = new EventEmitter<void>();

  private subscriptions: Subscription = new Subscription();
  private router: Router = inject(Router);
  private recordsDataRequestQueryParamsService: RecordsDataRequestQueryParamsService = inject(RecordsDataRequestQueryParamsService);
  private recordsTableToolbarService: RecordsTableToolbarService = inject(RecordsTableToolbarService);

  public readonly zoomOptions: Array<number> = ZOOM_OPTIONS;
  public readonly actionButtons = actionButtons;
  public readonly toggleGroupActionButtons = toggleGroupActionButtons;
  public searchValue: string = '';
  public isAddingNewRecord: boolean = false;

  public ngOnInit(): void {
    this.subscribeToRouterEvents();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public handleToggleChange(checked: boolean, key?: UpdateQueryParamsPropsKey): void {
    key ? this.changeState(checked, key) : this.changeEditModeState(checked);
  }

  public changeSearchValue(searchValue: string): void {
    this.updateQueryParams.next({
      key: 'search',
      value: searchValue
    });
  }

  public toggleAddRecordState(): void {
    this.isAddingNewRecord = !this.isAddingNewRecord;
    this.addRecordStateChanged.emit(this.isAddingNewRecord);
  }

  public handleCreateComponent(): void {
    this.createComponent.emit();
  }

  public handleCreateTestingResult(): void {
    this.createTestingResult.emit();
  }

  public activateAddQualityControl(): void {
    this.createQualityControl.emit();
  }

  public activateAddCooperationOffer(): void {
    this.addCooperationOffer.emit();
  }

  public activateAddUser(): void {
    this.addUser.emit();
  }

  public activeAddPriceLevelConfig(): void {
    this.addPriceLevelConfig.emit();
  }

  public handleCreateGroup(): void {
    this.createBloggerGroup.emit();
  }

  public handleOpenBloggerGroups(): void {
    this.openBloggerGroups.emit();
  }

  public handleSendMassOffers(): void {
    this.openSendMassOffers.emit();
  }

  public handleSaveButtonClick(): void {
    this.toggleAddRecordState();
    this.saveRecordButtonClicked.emit();
  }

  public handleZoomSelectValueChange(changeEvent: MatSelectChange): void {
    this.zoomChanged.emit(changeEvent.value);
  }

  public handleActionSelect(action: RecordTableAction): void {
    if (!this.recordsTableToolbarService.validateAction(action, this.selectedRowId)) { return; }

    this.actionSelected.emit(action);
  }

  public includesAction(action: RecordTableAction): boolean {
    return this.recordTableActions.includes(action);
  }

  public isZoomOptionsNeededForCurrentResourceType(key: string): boolean {
    return !(key in dropdownSettingsResources);
  }

  public refreshTableClick() {
    this.refreshTable.emit();
  }

  public activateAddTelegramAccount(): void {
    this.addTelegramAccount.emit();
  }

  public getToggleValue(key?: string): boolean {
    return key ? this.recordsDataRequestQueryParamsService.recordsDataRequestQueryParamsState()[key] : false;
  }

  public getToggleButtons(): Array<ToggleButtonsParams> {
    return toggleButtons.filter(item => this.includesAction(item.action));
  }

  private changeState(isMistakeShown: boolean, key: UpdateQueryParamsPropsKey): void {
    this.updateQueryParams.next({
      key: key,
      value: isMistakeShown
    });
  }

  private changeEditModeState(isEdit: boolean): void {
    this.editModeStateChange.next(isEdit);
  }

  private subscribeToRouterEvents(): void {
    this.subscriptions.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.resetSearchValue();
        }
      })
    );
  }

  private resetSearchValue(): void {
    this.searchValue = '';
  }
}
